import { CallBackProps, ACTIONS, STATUS, LIFECYCLE } from 'react-joyride';
import { OnboardingCaseEnum } from '../../../models/enums/onboarding-case-enum';
import { AddTradesOnboardingSubcaseEnum } from '../../../models/enums/add-trades-onboarding-subcase-enum';
import { waitForElementReady, waitForModalEvent } from '../../../utils/dom-observers';

// Enhanced logging for onboarding debugging - only when explicitly enabled
const logOnboardingEvent = (
    event: string,
    details: Record<string, unknown>,
    level: 'log' | 'warn' | 'error' = 'log',
) => {
    // Only log when explicitly enabled via debug flag - disabled by default
    const shouldLog = window.location.search.includes('onboarding-debug=true');

    if (!shouldLog) return;

    const logData = {
        timestamp: new Date().toISOString(),
        event,
        ...details,
    };

    // eslint-disable-next-line
    console[level](`[Onboarding] ${event}:`, logData);
};

export interface OnboardingHelperParams {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onboardingStore: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    tradeStore: any;
    setShowOverlay: (show: boolean) => void;
    validateTargetWithRetry?: (target: string, maxRetries?: number, delay?: number) => Promise<boolean>;
}

/**
 * Target validation with retry logic
 */
export const createValidateTargetWithRetry = (validateTarget: (target: string) => boolean) => {
    return async (target: string, maxRetries = 3, delay = 100): Promise<boolean> => {
        for (let attempt = 1; attempt <= maxRetries; attempt += 1) {
            if (validateTarget(target)) {
                return true;
            }

            if (attempt < maxRetries) {
                logOnboardingEvent(
                    'target_validation_retry',
                    {
                        target,
                        attempt,
                        maxRetries,
                    },
                    'warn',
                );

                // eslint-disable-next-line no-await-in-loop
                await new Promise<void>((resolve) => {
                    setTimeout(resolve, delay * attempt); // Exponential backoff
                });
            }
        }

        logOnboardingEvent(
            'target_validation_failed',
            {
                target,
                maxRetries,
            },
            'error',
        );
        return false;
    };
};

/**
 * Safety timeout for transitions
 */
export const createSafeTransitionTimeout = (
    isTransitioning: boolean,
    setIsTransitioning: (value: boolean) => void,
    setShowOverlay: (show: boolean) => void,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onboardingStore: any,
) => {
    return () => {
        const timeoutId = setTimeout(() => {
            if (isTransitioning) {
                logOnboardingEvent(
                    'transition_timeout_recovery',
                    {
                        stepIndex: onboardingStore.stepIndex,
                    },
                    'warn',
                );

                setIsTransitioning(false);
                setShowOverlay(false);

                // Try to recover by moving to next step or stopping onboarding
                if (onboardingStore.run) {
                    try {
                        onboardingStore.setStep(onboardingStore.stepIndex + 1);
                    } catch {
                        onboardingStore.stopOnboarding();
                    }
                }
            }
        }, 5000); // 5 second timeout

        return () => clearTimeout(timeoutId);
    };
};

/**
 * Handle Joyride errors with recovery attempts
 */
export const handleJoyrideError = async (
    data: CallBackProps,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onboardingStore: any,
    validateTargetWithRetry: (target: string) => Promise<boolean>,
): Promise<boolean> => {
    const { status, step, action, lifecycle, index } = data;

    const errorDetails = {
        status,
        step: step?.target,
        index,
        onboardingCase: onboardingStore.case,
        onboardingSubcase: onboardingStore.addTradesSubcase,
        stepIndex: onboardingStore.stepIndex,
        action,
        lifecycle,
    };

    // Always log errors - they're important for debugging
    logOnboardingEvent('joyride_error', errorDetails, 'error');

    // For target-related errors, try to recover instead of immediately stopping
    if (step?.target && step.target !== 'body') {
        const targetValid = await validateTargetWithRetry(step.target as string);
        if (targetValid) {
            logOnboardingEvent(
                'error_recovery_successful',
                {
                    target: step.target,
                },
                'warn',
            );
            return true; // Continue onboarding
        }

        logOnboardingEvent(
            'error_recovery_failed',
            {
                target: step.target,
            },
            'error',
        );
    }

    onboardingStore.stopOnboarding();
    return false; // Stop onboarding
};

/**
 * Handle target validation failures
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const handleTargetValidationFailure = (data: CallBackProps, onboardingStore: any): void => {
    const { step, index, action, lifecycle } = data;

    const validationFailureDetails = {
        target: step?.target,
        index,
        onboardingCase: onboardingStore.case,
        onboardingSubcase: onboardingStore.addTradesSubcase,
        action,
        lifecycle,
    };

    logOnboardingEvent('target_validation_failure', validationFailureDetails, 'warn');
    onboardingStore.stopOnboarding();
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const shouldSkipTargetValidation = (onboardingStore: any, index: number): boolean => {
    // Skip validation for manual sequence steps 6 and 7 (indexes 5 and 6)
    if (
        onboardingStore.case === OnboardingCaseEnum.AddTrades &&
        onboardingStore.addTradesSubcase === AddTradesOnboardingSubcaseEnum.manual &&
        (index === 5 || index === 6)
    ) {
        return true;
    }

    // Skip validation for import sequence steps 5 and 6 (indexes 4 and 5)
    if (
        onboardingStore.case === OnboardingCaseEnum.AddTrades &&
        onboardingStore.addTradesSubcase === AddTradesOnboardingSubcaseEnum.import &&
        (index === 4 || index === 5)
    ) {
        return true;
    }

    return false;
};

/**
 * Event-driven target waiting - waits for component ready events
 * Faster and more reliable than DOM polling
 */
const waitForTargetReady = async (
    targetSelector: string,
    modalType: 'import' | 'create',
    timeout = 3000,
): Promise<void> => {
    try {
        // First wait for the target ready event from the component
        await waitForModalEvent(`${modalType}-target-ready`, timeout);

        // Then verify the element is actually visible and ready
        await waitForElementReady(targetSelector, { timeout: 1000 }, { timeout: 1000 });

        logOnboardingEvent(`${modalType}_target_ready`, {
            target: targetSelector,
            modalType,
        });
    } catch (error) {
        logOnboardingEvent(
            `${modalType}_target_ready_failed`,
            {
                target: targetSelector,
                modalType,
                error: error instanceof Error ? error.message : String(error),
            },
            'warn',
        );

        // Fallback: try to find element directly (but without long delays)
        try {
            await waitForElementReady(targetSelector, { timeout: 500 }, { timeout: 500 });
            logOnboardingEvent(`${modalType}_target_fallback_success`, {
                target: targetSelector,
                modalType,
            });
        } catch (fallbackError) {
            throw new Error(`Target ${targetSelector} not ready after event and fallback attempts`);
        }
    }
};

export const handleModalOpeningTransition = async (
    data: CallBackProps,
    params: OnboardingHelperParams,
): Promise<boolean> => {
    const { action, index, lifecycle } = data;
    const { onboardingStore, tradeStore, setShowOverlay } = params;

    if (action !== ACTIONS.NEXT || lifecycle !== LIFECYCLE.COMPLETE) return false;
    if (onboardingStore.case !== OnboardingCaseEnum.AddTrades || index !== 0) return false;

    const nextStepIndex = index + 1;
    setShowOverlay(true);

    try {
        logOnboardingEvent('modal_opening_transition_start', {
            subcase: onboardingStore.addTradesSubcase,
            currentStep: index,
            nextStep: nextStepIndex,
        });

        if (onboardingStore.addTradesSubcase === AddTradesOnboardingSubcaseEnum.import) {
            const { tradeImportDialogStore } = tradeStore;

            // Use new Promise-based API for faster modal opening
            await tradeImportDialogStore.openImportDialogAsync();

            logOnboardingEvent('import_modal_opened_via_promise', {
                dialogState: tradeImportDialogStore.opened,
            });

            // Wait for target to be ready using event-driven approach
            await waitForTargetReady('#import-trade-file-input', 'import');
        } else if (onboardingStore.addTradesSubcase === AddTradesOnboardingSubcaseEnum.manual) {
            const { tradeDialogStore } = tradeStore;

            // Use new Promise-based API for faster modal opening
            await tradeDialogStore.openCreateDialogAsync();
            tradeDialogStore.fetchSymbols();

            logOnboardingEvent('create_modal_opened_via_promise', {
                dialogState: tradeDialogStore.createDialogOpened,
            });

            // Wait for target to be ready using event-driven approach
            await waitForTargetReady('#create-trade-symbol', 'create');
        }

        // Move to next step immediately - no unnecessary delays
        onboardingStore.setStep(nextStepIndex);
        setShowOverlay(false);

        logOnboardingEvent('modal_opening_transition_completed', {
            subcase: onboardingStore.addTradesSubcase,
            finalStep: nextStepIndex,
        });

        return true; // Indicates transition was handled
    } catch (error) {
        logOnboardingEvent(
            'modal_opening_transition_error',
            {
                error: error instanceof Error ? error.message : String(error),
                subcase: onboardingStore.addTradesSubcase,
                currentStep: index,
            },
            'error',
        );
        setShowOverlay(false);
        return false;
    }
};

export const handleModalClosingTransition = async (
    data: CallBackProps,
    params: OnboardingHelperParams,
): Promise<boolean> => {
    const { action, index, lifecycle } = data;
    const { onboardingStore, tradeStore, setShowOverlay } = params;

    if (action !== ACTIONS.NEXT || lifecycle !== LIFECYCLE.COMPLETE) return false;
    if (onboardingStore.case !== OnboardingCaseEnum.AddTrades) return false;

    const nextStepIndex = index + 1;

    try {
        // Handle manual sequence: close create dialog when moving from step 6 to 7
        if (onboardingStore.addTradesSubcase === AddTradesOnboardingSubcaseEnum.manual && index === 5) {
            setShowOverlay(true);

            const { tradeDialogStore } = tradeStore;
            tradeDialogStore.closeCreateDialog();

            // No polling needed - modal closes immediately
            onboardingStore.setStep(nextStepIndex);
            setShowOverlay(false);

            return true;
        }

        // Handle import sequence: close import dialog when moving from step 4 to 5
        if (onboardingStore.addTradesSubcase === AddTradesOnboardingSubcaseEnum.import && index === 3) {
            setShowOverlay(true);

            const { tradeImportDialogStore } = tradeStore;
            tradeImportDialogStore.closeImportDialog();

            // No polling needed - modal closes immediately
            onboardingStore.setStep(nextStepIndex);
            setShowOverlay(false);

            return true;
        }

        return false;
    } catch (error) {
        // eslint-disable-next-line
        console.error('Error during modal closing transition:', error);
        setShowOverlay(false);
        return false;
    }
};

export const handleBackwardNavigation = async (
    data: CallBackProps,
    params: OnboardingHelperParams,
): Promise<boolean> => {
    const { action, index, lifecycle } = data;
    const { onboardingStore, tradeStore, setShowOverlay } = params;

    if (action !== ACTIONS.PREV || lifecycle !== LIFECYCLE.COMPLETE) return false;

    const prevStepIndex = index - 1;

    try {
        // Handle going back from import/manual sequences to start
        if (onboardingStore.case === OnboardingCaseEnum.AddTrades && index === 0 && prevStepIndex === -1) {
            if (
                onboardingStore.addTradesSubcase === AddTradesOnboardingSubcaseEnum.import ||
                onboardingStore.addTradesSubcase === AddTradesOnboardingSubcaseEnum.manual
            ) {
                onboardingStore.setAddTradesSubcase(AddTradesOnboardingSubcaseEnum.start);
                return true;
            }
        }

        // Close modal when going back from second step to first step in import/manual sequences
        if (onboardingStore.case === OnboardingCaseEnum.AddTrades && index === 1 && prevStepIndex === 0) {
            setShowOverlay(true);

            if (onboardingStore.addTradesSubcase === AddTradesOnboardingSubcaseEnum.import) {
                const { tradeImportDialogStore } = tradeStore;
                tradeImportDialogStore.closeImportDialog();
            } else if (onboardingStore.addTradesSubcase === AddTradesOnboardingSubcaseEnum.manual) {
                const { tradeDialogStore } = tradeStore;
                tradeDialogStore.closeCreateDialog();
            }
            onboardingStore.setStep(prevStepIndex);
            setShowOverlay(false);
            return true;
        }

        // Reopen modal when going back from step 7 to step 6 in manual sequence
        if (
            onboardingStore.case === OnboardingCaseEnum.AddTrades &&
            onboardingStore.addTradesSubcase === AddTradesOnboardingSubcaseEnum.manual &&
            index === 6 &&
            prevStepIndex === 5
        ) {
            setShowOverlay(true);

            const { tradeDialogStore } = tradeStore;
            await tradeDialogStore.openCreateDialogAsync();
            tradeDialogStore.fetchSymbols();
            onboardingStore.setStep(prevStepIndex);
            setShowOverlay(false);
            return true;
        }

        // Reopen modal when going back from step 5 to step 4 in import sequence
        if (
            onboardingStore.case === OnboardingCaseEnum.AddTrades &&
            onboardingStore.addTradesSubcase === AddTradesOnboardingSubcaseEnum.import &&
            index === 4 &&
            prevStepIndex === 3
        ) {
            setShowOverlay(true);

            const { tradeImportDialogStore } = tradeStore;
            await tradeImportDialogStore.openImportDialogAsync();
            onboardingStore.setStep(prevStepIndex);
            setShowOverlay(false);
            return true;
        }

        // Default backward navigation
        if (prevStepIndex >= 0) {
            onboardingStore.setStep(prevStepIndex);
            return true;
        }

        return false;
    } catch (error) {
        // eslint-disable-next-line
        console.error('Error during backward navigation:', error);
        setShowOverlay(false);
        return false;
    }
};

export const isOnboardingFinished = (data: CallBackProps): boolean => {
    const { status, action, lifecycle } = data;

    const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];
    const finishedActions: string[] = [ACTIONS.CLOSE, ACTIONS.SKIP];

    return finishedStatuses.includes(status) || (finishedActions.includes(action) && lifecycle === LIFECYCLE.COMPLETE);
};
